// MemoryMagnetEdit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  UploadCloudIcon,
  MinusIcon,
  PlusIcon,
  CartIcon
} from '../../../public/icons';
import PersonalizeModal from '../../../component/MemoryMagnets/PersonalizeModal';
import ReviewModal from '../../../component/general/ReviewModal';
import CustomerReviews from '../../../component/general/CustomerReviews';

/* ─────────────────────────────────────────────
   Draft persistence helper
   ───────────────────────────────────────────── */
const DRAFT_PREFIX = 'kapta_draft_';
const MODAL_DRAFT_KEY = 'kapta_personalize_modal_draft';

const loadDraft = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load draft:', err);
    return null;
  }
};

const saveDraft = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save draft:', err);
  }
};

const clearDraftByKey = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.error('Failed to clear draft:', err);
  }
};

const MemoryMagnetEdit = () => {
  const location = useLocation();

  const {
    id,
    isCustom = false,
    magnetId,
    productFamily = 'Memory Magnets',
    collectionTitle = null,
    title = 'Custom Memory Magnet',
    description =
      'Upload your photo, write your story, and turn it into a magnet that holds meaning.',
    image = null,
    category,
    inStock = true,
    price = 30000,
    location: magnetLocation = 'Various Locations',
    detailedDescription = ''
  } = location.state || {};

  /* ── Is this the "Names of God & their Meaning" product? ── */
  const isNamesOfGodProduct = id === 'names-and-meaning';

  /* ── Stable draft key for this product ── */
  const draftKey =
    `${DRAFT_PREFIX}${location.pathname
      .replace(/^\//, '')
      .replace(/\//g, '-')
      .replace(/-+/g, '-')}` ||
    `${DRAFT_PREFIX}memory-magnets_default`;

  /* ── State ── */
  const [quantity, setQuantity] = useState(1);
  const [personalizedMessage, setPersonalizedMessage] = useState('');
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [hoveredThumb, setHoveredThumb] = useState(null);
  const [mainImageHovered, setMainImageHovered] = useState(false);

  const [personalizedMagnets, setPersonalizedMagnets] = useState([]);
  const [uploadedImages, setUploadedImages] = useState(isCustom ? [] : [image]);
  const [stories, setStories] = useState(isCustom ? [] : ['']);

  const [reviewsRefreshKey, setReviewsRefreshKey] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  const [isAdding, setIsAdding] = useState(false);

  const [personalizeModalKey, setPersonalizeModalKey] = useState(0);

  /* ── Names-of-God: inline custom form state ── */
  const [customizeThisMagnet, setCustomizeThisMagnet] = useState(false);
  const [activeSlot, setActiveSlot] = useState(0);

  const [magnetSlots, setMagnetSlots] = useState([
    { name: '', meaning: '', story: '' },
    { name: '', meaning: '', story: '' },
    { name: '', meaning: '', story: '' },
    { name: '', meaning: '', story: '' }
  ]);

  const draftHydratedRef = useRef(false);

  /* ─────────────────────────────────────────────
     1. HYDRATE from localStorage on mount
     ───────────────────────────────────────────── */
  useEffect(() => {
    const draft = loadDraft(draftKey);
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.quantity === 'number') setQuantity(draft.quantity);
    if (typeof draft.personalizedMessage === 'string')
      setPersonalizedMessage(draft.personalizedMessage);
    if (Array.isArray(draft.personalizedMagnets))
      setPersonalizedMagnets(draft.personalizedMagnets);
    if (Array.isArray(draft.uploadedImages))
      setUploadedImages(draft.uploadedImages);
    if (Array.isArray(draft.stories)) setStories(draft.stories);
    if (typeof draft.heroIndex === 'number') setHeroIndex(draft.heroIndex);
    if (typeof draft.customizeThisMagnet === 'boolean')
      setCustomizeThisMagnet(draft.customizeThisMagnet);
    if (typeof draft.activeSlot === 'number') setActiveSlot(draft.activeSlot);
    if (Array.isArray(draft.magnetSlots) && draft.magnetSlots.length === 4)
      setMagnetSlots(draft.magnetSlots);

    draftHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  /* ─────────────────────────────────────────────
     2. Also hydrate personalizedMagnets from the
        modal draft if the outer draft is empty
        (covers the "closed modal without saving" case)
     ───────────────────────────────────────────── */
  useEffect(() => {
    const outerDraft = loadDraft(draftKey);
    const outerHasMagnets =
      outerDraft &&
      Array.isArray(outerDraft.personalizedMagnets) &&
      outerDraft.personalizedMagnets.length > 0;

    if (outerHasMagnets) return;

    try {
      const modalRaw = localStorage.getItem(MODAL_DRAFT_KEY);
      if (!modalRaw) return;
      const modalDraft = JSON.parse(modalRaw);

      const inProgress =
        modalDraft.memoryTitle?.trim() ||
        modalDraft.memoryStory?.trim() ||
        (modalDraft.uploadedImages && modalDraft.uploadedImages.length > 0)
          ? {
              memoryTitle: modalDraft.memoryTitle?.trim() || '',
              memoryStory: modalDraft.memoryStory?.trim() || '',
              uploadedImages: modalDraft.uploadedImages || []
            }
          : null;

      const combined = [
        ...(modalDraft.allMagnets || []),
        ...(inProgress ? [inProgress] : [])
      ];

      if (combined.length > 0) {
        setPersonalizedMagnets(combined);
        const images = combined
          .map((m) => m?.uploadedImages?.[0]?.url)
          .filter(Boolean);
        setUploadedImages(images);
        setStories(combined.map((m) => m.memoryStory || ''));
        const firstWithImage = combined.findIndex(
          (m) => m?.uploadedImages?.[0]?.url
        );
        if (firstWithImage >= 0) setHeroIndex(firstWithImage);
      }
    } catch (err) {
      console.error('Failed to hydrate modal draft on mount:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  /* ─────────────────────────────────────────────
     3. AUTOSAVE to localStorage on every change
     ───────────────────────────────────────────── */
  useEffect(() => {
    if (!draftHydratedRef.current) return;

    const draft = {
      quantity,
      personalizedMessage,
      personalizedMagnets,
      uploadedImages,
      stories,
      heroIndex,
      customizeThisMagnet,
      activeSlot,
      magnetSlots
    };
    saveDraft(draftKey, draft);
  }, [
    draftKey,
    quantity,
    personalizedMessage,
    personalizedMagnets,
    uploadedImages,
    stories,
    heroIndex,
    customizeThisMagnet,
    activeSlot,
    magnetSlots
  ]);

  /* 🟢 Save on refresh / tab close as belt-and-suspenders */
  useEffect(() => {
    const saveNow = () => {
      if (!draftHydratedRef.current) return;
      saveDraft(draftKey, {
        quantity,
        personalizedMessage,
        personalizedMagnets,
        uploadedImages,
        stories,
        heroIndex,
        customizeThisMagnet,
        activeSlot,
        magnetSlots
      });
    };
    window.addEventListener('beforeunload', saveNow);
    return () => window.removeEventListener('beforeunload', saveNow);
  }, [
    draftKey,
    quantity,
    personalizedMessage,
    personalizedMagnets,
    uploadedImages,
    stories,
    heroIndex,
    customizeThisMagnet,
    activeSlot,
    magnetSlots
  ]);

  const updateSlot = (index, field, value) => {
    setMagnetSlots((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const isNamesOfGodCustomMode = isNamesOfGodProduct && customizeThisMagnet;

  const displayTitle = isNamesOfGodProduct
    ? isNamesOfGodCustomMode
      ? 'Names of God & their Meaning'
      : title
    : title;

  const displayDescription = isNamesOfGodProduct
    ? isNamesOfGodCustomMode
      ? `Every name carries history. Memory. Identity.
This Kapta magnet set is inspired by the meanings behind sacred names, a quiet reminder of who we are becoming and what we carry with us.
Add your name, its meaning, and a story or gift it to someone whose name holds weight in your heart.`
      : description
    : description;

  const PRODUCT_SLUG =
    magnetId || id || title.toLowerCase().replace(/\s+/g, '-');

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  /* ── Self-correct heroIndex ── */
  useEffect(() => {
    if (personalizedMagnets.length === 0) {
      if (heroIndex !== 0) setHeroIndex(0);
      return;
    }

    const currentHasImage = !!personalizedMagnets[heroIndex]
      ?.uploadedImages?.[0]?.url;

    if (!currentHasImage) {
      const firstWithImage = personalizedMagnets.findIndex(
        (m) => m?.uploadedImages?.[0]?.url
      );
      if (firstWithImage >= 0 && firstWithImage !== heroIndex) {
        setHeroIndex(firstWithImage);
      }
    }
  }, [personalizedMagnets, heroIndex]);

  /* ── Personalize SAVE (final, from modal) ── */
  const handlePersonalizeSave = (magnets) => {
    const list = Array.isArray(magnets) ? magnets : [];

    setPersonalizedMagnets(list);

    const firstWithImage = list.findIndex(
      (m) => m?.uploadedImages?.[0]?.url
    );
    setHeroIndex(firstWithImage >= 0 ? firstWithImage : 0);

    const images = list
      .map((m) => m?.uploadedImages?.[0]?.url)
      .filter(Boolean);
    setUploadedImages(images);
    setStories(list.map((m) => m.memoryStory || ''));
  };

  /* ── Personalize PROGRESS ── */
  const handlePersonalizeProgress = (progress) => {
    if (!progress) return;

    const inProgressMagnet =
      progress.memoryTitle?.trim() ||
      progress.memoryStory?.trim() ||
      (progress.uploadedImages && progress.uploadedImages.length > 0)
        ? {
            memoryTitle: progress.memoryTitle?.trim() || '',
            memoryStory: progress.memoryStory?.trim() || '',
            uploadedImages: progress.uploadedImages || []
          }
        : null;

    const combinedMagnets = [
      ...(progress.allMagnets || []),
      ...(inProgressMagnet ? [inProgressMagnet] : [])
    ];

    setPersonalizedMagnets(combinedMagnets);

    const images = combinedMagnets
      .map((m) => m?.uploadedImages?.[0]?.url)
      .filter(Boolean);
    setUploadedImages(images);
    setStories(combinedMagnets.map((m) => m.memoryStory || ''));

    const firstWithImage = combinedMagnets.findIndex(
      (m) => m?.uploadedImages?.[0]?.url
    );
    if (firstWithImage >= 0) setHeroIndex(firstWithImage);
  };

  /* ── Reset ── */
  const resetDesign = () => {
    setPersonalizedMagnets([]);
    setUploadedImages([]);
    setStories([]);
    setHeroIndex(0);
    setQuantity(1);
    setPersonalizedMessage('');
    setCustomizeThisMagnet(false);
    setActiveSlot(0);
    setMagnetSlots([
      { name: '', meaning: '', story: '' },
      { name: '', meaning: '', story: '' },
      { name: '', meaning: '', story: '' },
      { name: '', meaning: '', story: '' }
    ]);
    setPersonalizeModalKey((k) => k + 1);
  };

  /* ── Add to bag ── */
  const handleAddToBag = async () => {
    if (isNamesOfGodCustomMode) {
      const hasAtLeastOne = magnetSlots.some(
        (s) => s.name.trim() && s.meaning.trim()
      );
      if (!hasAtLeastOne) {
        showToast('Add at least one name and its meaning to continue', 'error');
        return;
      }
    }

    if (isCustom && !isNamesOfGodProduct && personalizedMagnets.length === 0) {
      showToast('Add at least one magnet to continue', 'error');
      return;
    }

    if (!inStock) {
      showToast('This item is out of stock', 'error');
      return;
    }
    if (isAdding) return;

    try {
      setIsAdding(true);
      await new Promise((resolve) => setTimeout(resolve, 400));

      const orderTitle = isNamesOfGodCustomMode
        ? 'Names of God & their Meaning'
        : isCustom && !isNamesOfGodProduct
        ? 'Custom Memory Magnet'
        : title;

      const firstMagnetWithImage = personalizedMagnets.find(
        (m) => m?.uploadedImages?.[0]?.url
      );

      const filledSlots = magnetSlots.filter(
        (s) => s.name.trim() || s.meaning.trim() || s.story.trim()
      );

      const namesOfGodCustomization = isNamesOfGodCustomMode
        ? {
            magnetType: 'names-of-god',
            customDetails: filledSlots.map((s) => ({
              name: s.name.trim(),
              meaning: s.meaning.trim(),
              story: s.story.trim()
            })),
            magnets: filledSlots.map((s) => ({
              memoryTitle: s.name.trim(),
              memoryStory: `${s.meaning.trim()}${
                s.story.trim() ? `\n\n${s.story.trim()}` : ''
              }`,
              uploadedImages: []
            }))
          }
        : null;

      const cartItem = {
        cartItemId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id: id || magnetId || PRODUCT_SLUG,
        slug: id || PRODUCT_SLUG,
        title: orderTitle,

        magnetId: magnetId || null,
        magnetName: isCustom && !isNamesOfGodProduct ? null : title,
        collectionName:
          isCustom && !isNamesOfGodProduct ? null : collectionTitle,

        description: displayDescription,
        isCustom: isCustom || isNamesOfGodCustomMode,
        quantity,
        price,
        lineTotal: price * quantity,
        image:
          isCustom && !isNamesOfGodProduct
            ? firstMagnetWithImage?.uploadedImages?.[0]?.url || image || null
            : image,

        ...(isNamesOfGodProduct
          ? {
              customization:
                namesOfGodCustomization || {
                  magnetType: 'names-of-god',
                  curated: true
                },
              customNameDetails: isNamesOfGodCustomMode
                ? filledSlots.map((s) => ({
                    name: s.name.trim(),
                    meaning: s.meaning.trim(),
                    story: s.story.trim()
                  }))
                : [],
              customizeThisMagnet
            }
          : isCustom
          ? {
              magnets: personalizedMagnets,
              customization: {
                magnetType: 'upload-own',
                magnets: personalizedMagnets
              }
            }
          : { personalizedMessage })
      };

      const existing = JSON.parse(
        localStorage.getItem('memoryMagnetsCart') || '[]'
      );
      existing.push(cartItem);
      localStorage.setItem('memoryMagnetsCart', JSON.stringify(existing));

      clearDraftByKey(draftKey);
      localStorage.removeItem(MODAL_DRAFT_KEY);

      window.dispatchEvent(new Event('cartUpdated'));
      window.dispatchEvent(new CustomEvent('openShoppingBag'));

      showToast('Added to bag', 'success');
      resetDesign();
    } catch (err) {
      console.error('Failed to add to bag:', err);
      showToast('Failed to add to bag. Please try again.', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  /* ── Hover messages ── */
  const getHoverMessage = (t) => {
    if (t.includes('Yankari')) return 'Yankari Game Reserve is a wildlife paradise';
    if (t.includes('Obudu')) return 'Obudu Mountain Resort offers breathtaking views';
    if (t.includes('Olumo')) return 'Olumo Rock tells ancient stories';
    if (t.includes('Idanre')) return 'Idanre Hills hold historical treasures';
    if (t.includes('Erin Ijesha')) return "Erin Ijesha Waterfalls is nature's wonder";
    if (t.includes('Zuma')) return 'Zuma Rock is an iconic landmark';
    if (t.includes('Eiffel')) return 'Eiffel Tower is a Parisian masterpiece';
    if (t.includes('Louvre')) return 'Louvre Museum holds artistic treasures';
    if (t.includes('Notre-Dame')) return 'Notre-Dame Cathedral is architectural beauty';
    if (t.includes('Niagara')) return 'Niagara Falls is a natural wonder';
    if (t.includes('CN Tower')) return 'CN Tower offers stunning city views';
    if (t.includes('Taj Mahal')) return 'Taj Mahal is a symbol of eternal love';
    if (t.includes('Gateway')) return 'Gateway of India is a historic monument';
    if (t.includes('Big Ben')) return "Big Ben is London's iconic timekeeper";
    if (t.includes('London Eye')) return 'London Eye provides breathtaking views';
    if (t.includes('Waikiki')) return 'Waikiki Beach is a tropical paradise';
    if (t.includes('Volcano')) return "Hawaii Volcanoes are nature's power";
    if (t.includes('Names of God')) return 'Every name carries history. Memory. Identity.';
    if (t.includes('African Names')) return 'African names carry deep meanings';
    if (t.includes('Biblical Names')) return 'Biblical names have spiritual significance';
    if (t.includes('Custom')) return 'Add Your Custom Story Here';
    return `${t} is beautiful`;
  };

  /* ───────── Render image section ───────── */
  const renderImageSection = () => {
    if (isNamesOfGodCustomMode) {
      // ... (Keep existing Names of God image rendering)
      const active = magnetSlots[activeSlot];
      const hasActiveDetails = !!(active.name.trim() || active.meaning.trim());

      return (
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex flex-row sm:flex-col gap-2 sm:gap-3 order-2 sm:order-1">
              {magnetSlots.map((slot, i) => {
                const filled = !!(slot.name.trim() || slot.meaning.trim());
                const isActive = i === activeSlot;

                return (
                  <div key={i} className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setActiveSlot(i)}
                      className={`w-full h-full flex flex-col items-center justify-center text-center px-1.5 py-1 transition-all overflow-hidden ${
                        filled
                          ? 'bg-[#1C2A46] text-[#FCF7EE]'
                          : 'border-2 border-dashed border-gray-300 hover:border-[#D4A574] text-gray-400'
                      } ${isActive ? 'ring-2 ring-[#D4A574] ring-offset-1' : ''}`}
                    >
                      {filled ? (
                        <>
                          <span className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70 leading-none mb-1">
                            Your Name
                          </span>
                          <span className="text-[10px] sm:text-xs font-semibold leading-tight truncate w-full">
                            {slot.name.trim() || '—'}
                          </span>
                          {slot.meaning.trim() && (
                            <>
                              <span className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70 leading-none mt-1 mb-0.5">
                                Which means
                              </span>
                              <span className="text-[9px] sm:text-[10px] leading-tight truncate w-full opacity-90">
                                {slot.meaning.trim()}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <PlusIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                          <span className="text-[9px] sm:text-[10px] mt-1 opacity-80">
                            Magnet {i + 1}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            <div
              className="w-full sm:w-[400px] md:w-[500px] h-[400px] sm:h-[500px] md:h-[600px] cursor-pointer relative order-1 sm:order-2 overflow-hidden bg-[#F5F5F5] border border-[#E4E4E4]"
              onClick={() => {
                document
                  .getElementById('names-of-god-form')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {hasActiveDetails ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-6 sm:p-8 text-center">
                  <p className="text-xs sm:text-sm uppercase tracking-widest text-[#8A9099] mb-4">
                    Magnet {activeSlot + 1}
                  </p>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C2A46] mb-4 break-words">
                    {active.name.trim() || 'Your Name'}
                  </h3>
                  {active.meaning.trim() && (
                    <p className="text-sm sm:text-base text-[#6B635D] italic max-w-md mb-6">
                      “{active.meaning.trim()}”
                    </p>
                  )}
                  {active.story.trim() && (
                    <p className="text-xs sm:text-sm text-[#8A9099] max-w-md line-clamp-4">
                      {active.story.trim()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="w-full h-full bg-[#1C2A46] flex items-center justify-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center">
                    <PlusIcon className="w-6 h-6 sm:w-8 sm:h-8 text-[#1C2A46]" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (isCustom && !isNamesOfGodProduct) {
      // ... (Keep existing custom upload image rendering)
      const magnetImageByIndex = personalizedMagnets.map(
        (m) => m?.uploadedImages?.[0]?.url || null
      );

      const firstFilledIndex = magnetImageByIndex.findIndex(Boolean);

      const safeHeroIndex = magnetImageByIndex[heroIndex]
        ? heroIndex
        : firstFilledIndex >= 0
        ? firstFilledIndex
        : 0;

      const heroImage = magnetImageByIndex[safeHeroIndex] || null;

      const thumbs = Array.from({ length: 4 }, (_, i) => ({
        url: magnetImageByIndex[i] || null,
        magnetIndex: i
      }));

      return (
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex flex-row sm:flex-col gap-2 sm:gap-3 order-2 sm:order-1">
              {thumbs.map(({ url, magnetIndex }) => {
                const isSelected = url && magnetIndex === safeHeroIndex;

                return (
                  <div key={magnetIndex} className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
                    <div className="w-full h-full relative">
                      {url ? (
                        <img
                          src={url}
                          alt={`Magnet ${magnetIndex + 1}`}
                          className={`w-full h-full object-cover cursor-pointer transition-all ${
                            isSelected ? 'ring-2 ring-[#D4A574]' : 'hover:opacity-80'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setHeroIndex(magnetIndex);
                          }}
                        />
                      ) : (
                        <div
                          className="w-full h-full border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#D4A574] transition-colors"
                          onClick={() => setShowPersonalizeModal(true)}
                        >
                          <PlusIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className="w-full sm:w-[400px] md:w-[500px] h-[400px] sm:h-[500px] md:h-[600px] cursor-pointer relative order-1 sm:order-2 overflow-hidden"
              onClick={() => setShowPersonalizeModal(true)}
            >
              {heroImage ? (
                <img src={heroImage} alt="Main magnet preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#1C2A46] flex items-center justify-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center">
                    <PlusIcon className="w-6 h-6 sm:w-8 sm:h-8 text-[#1C2A46]" />
                  </div>
                </div>
              )}

              {personalizedMagnets.length > 0 && (
                <div className="absolute bottom-3 right-3 bg-black bg-opacity-60 text-white text-xs sm:text-sm px-3 py-1.5 rounded-full">
                  {personalizedMagnets.length} magnet{personalizedMagnets.length !== 1 ? 's' : ''} added
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // ... (Keep existing pre-made image rendering)
    return (
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="flex flex-row sm:flex-col gap-2 sm:gap-2.5 order-2 sm:order-1">
          {[1, 2, 3, 4].map((thumb) => (
            <div key={thumb} className="w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 relative">
              <div
                className="flip-card-thumb w-full h-full"
                onMouseEnter={() => setHoveredThumb(thumb)}
                onMouseLeave={() => setHoveredThumb(null)}
              >
                <div className={`flip-card-front-thumb ${hoveredThumb === thumb ? 'flipped' : ''}`}>
                  <img src={image} alt={`Thumbnail ${thumb}`} className="w-full h-full object-cover" />
                </div>

                <div className={`flip-card-back-thumb ${hoveredThumb === thumb ? 'flipped' : ''}`}>
                  <div className="w-full h-full bg-black flex items-center justify-center">
                    <p className="text-white text-xs p-1 sm:p-2 text-center">Preview {thumb}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="w-full sm:w-[350px] md:w-[450px] lg:w-[498px] h-[350px] sm:h-[450px] md:h-[550px] lg:h-[632px] overflow-hidden relative order-1 sm:order-2">
          <div
            className="flip-card-main w-full h-full"
            onMouseEnter={() => setMainImageHovered(true)}
            onMouseLeave={() => setMainImageHovered(false)}
          >
            <div className={`flip-card-front-main ${mainImageHovered ? 'flipped' : ''}`}>
              <img src={image} alt={title} className="w-full h-full object-cover" />
            </div>

            <div className={`flip-card-back-main ${mainImageHovered ? 'flipped' : ''}`}>
              <div className="w-full h-full bg-black flex items-center justify-center p-4 sm:p-6">
                <div className="text-center px-2 sm:px-4">
                  <p className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-medium leading-tight">
                    {getHoverMessage(title)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isAddToBagEnabled = () => {
    if (isNamesOfGodCustomMode) {
      return magnetSlots.some((s) => s.name.trim() && s.meaning.trim());
    }
    if (isCustom && !isNamesOfGodProduct) {
      return personalizedMagnets.length > 0;
    }
    return true;
  };

  const renderAddToBagButton = () => {
    const blocked = !isAddToBagEnabled() || !inStock || isAdding;

    let label = 'Add to Bag';
    if (isAdding) label = 'Adding…';
    else if (!inStock) label = 'Out of Stock';
    else if (!isAddToBagEnabled())
      label = isNamesOfGodCustomMode ? 'Add to Bag' : 'Add to Bag';

    let className =
      'p-3 text-base sm:text-base font-medium w-full sm:w-full transition-colors flex items-center justify-center space-x-2';

    if (isAdding) {
      className += ' bg-[#C4956A] text-[#FCF7EE] cursor-wait';
    } else if (blocked) {
      className += ' bg-[#DBDBDB] text-white cursor-not-allowed';
    } else {
      className += ' bg-[#D4A574] text-[#FCF7EE] hover:bg-[#C4956A]';
    }

    return (
      <button onClick={handleAddToBag} disabled={blocked} className={className}>
        {isAdding ? (
          <>
            <span className="inline-block w-5 h-5 border-2 border-[#FCF7EE] border-t-transparent rounded-full animate-spin" />
            <span>Adding…</span>
          </>
        ) : (
          <>
            <CartIcon />
            <span>{label}</span>
          </>
        )}
      </button>
    );
  };

  /* ── DYNAMIC BREADCRUMB LOGIC ── */
  const breadcrumbParent = productFamily !== 'Memory Magnets' ? productFamily : 'Shop';
  const breadcrumbParentLink = productFamily !== 'Memory Magnets' ? '/shop' : '/shop';
  
  // Only show the parent collection if we're in a sub-collection (like Names & Meaning or Heritage)
  const showParentCollection = 
    collectionTitle && 
    collectionTitle !== title && 
    collectionTitle !== productFamily;

  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      {/* Toast */}
      {toast.show && (
        <div
          className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999]"
          style={{ animation: 'fadeInDown 0.3s ease-out' }}
        >
          <div
            className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-xl border ${
              toast.type === 'success'
                ? 'bg-[#E7F4ED] border-[#2F8F5B] text-[#2F8F5B]'
                : toast.type === 'error'
                ? 'bg-[#F4E7E7] border-[#A82121] text-[#A82121]'
                : 'bg-[#E7ECF4] border-[#2F578F] text-[#2F578F]'
            }`}
          >
            {toast.type === 'success' && (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="#2F8F5B" strokeWidth="2" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="#2F8F5B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span className="font-medium text-sm sm:text-base">
              {toast.message}
            </span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 max-w-[78rem] pt-10">
        
        {/* ✅ DYNAMIC BREADCRUMB */}
        <nav className="flex flex-wrap items-center gap-x-3 gap-y-2 text-base mb-10 text-left">
          <Link to="/shop" className="text-[#6B635D] hover:text-[#1C2A46] transition-colors">
            Shop
          </Link>
          
          {breadcrumbParent !== 'Shop' && (
            <>
              <span className="text-[#6B635D]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </span>
              <Link to={breadcrumbParentLink} className="text-[#6B635D] hover:text-[#1C2A46] transition-colors">
                {breadcrumbParent}
              </Link>
            </>
          )}

          {showParentCollection && (
            <>
              <span className="text-[#6B635D]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </span>
              <span className="text-[#6B635D]">
                {collectionTitle}
              </span>
            </>
          )}

          <span className="text-[#6B635D]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </span>
          <span className="text-[#1C2A46] font-medium">
            {title}
          </span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="w-full lg:w-auto">{renderImageSection()}</div>

          <div className="w-full lg:flex-1">
            {isNamesOfGodProduct && (
              <label className="flex items-start gap-3 cursor-pointer mb-6 sm:mb-8">
                <input
                  type="checkbox"
                  checked={customizeThisMagnet}
                  onChange={(e) => setCustomizeThisMagnet(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#1C2A46] cursor-pointer"
                />
                <div className="text-left">
                  <p className="font-normal text-[#1C2A46] text-sm sm:text-base">
                    I want to customise this magnet
                  </p>
                  <p className="text-xs sm:text-sm text-[#6B635D] font-normal mt-0.5">
                    If you don't customise, you'll receive the original curated
                    design and meaning.
                  </p>
                </div>
              </label>
            )}

            {isNamesOfGodCustomMode || (isCustom && !isNamesOfGodProduct) ? (
              <div className="space-y-6 sm:space-y-8 text-left">
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C2A46] mb-2">
                    {displayTitle}
                  </h1>
                  <p className="text-base sm:text-lg text-[#6B635D] leading-relaxed mb-4 whitespace-pre-line">
                    {displayDescription}
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center justify-between space-x-4 w-32 sm:w-36 p-1.5 border border-[#E0E0E0]">
                      <button
                        onClick={() => setQuantity((p) => Math.max(1, p - 1))}
                        className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                        disabled={isAdding}
                      >
                        <MinusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                      </button>
                      <span className="text-sm sm:text-base font-normal text-[#1C2A46] min-w-[2.5rem] sm:min-w-[3rem] text-center">
                        {quantity} set
                      </span>
                      <button
                        onClick={() => setQuantity((p) => p + 1)}
                        className="rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                        disabled={isAdding}
                      >
                        <PlusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                      </button>
                    </div>
                  </div>

                  <p className="text-xs sm:text-sm text-[#6B635D] italic mb-6 sm:mb-7">
                    Each set contains 4 memory magnets
                  </p>

                  {isNamesOfGodCustomMode ? (
                    <div id="names-of-god-form" className="mb-6 sm:mb-8">
                      <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
                        {magnetSlots.map((slot, i) => {
                          const filled = !!(
                            slot.name.trim() || slot.meaning.trim()
                          );
                          const isActive = i === activeSlot;
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setActiveSlot(i)}
                              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium whitespace-nowrap border transition-colors ${
                                isActive
                                  ? 'bg-[#1C2A46] text-white border-[#1C2A46]'
                                  : filled
                                  ? 'bg-[#E7F4ED] text-[#2F8F5B] border-[#2F8F5B]'
                                  : 'bg-white text-[#6B635D] border-[#DBDBDB] hover:border-[#D4A574]'
                              }`}
                            >
                              Magnet {i + 1}
                              {filled && !isActive ? ' ✓' : ''}
                            </button>
                          );
                        })}
                      </div>

                      <h3 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-1">
                        Custom Details for Magnet {activeSlot + 1}
                      </h3>
                      <p className="text-sm sm:text-base text-[#6B635D] mb-5">
                        Add your name, meaning, and a short story.
                      </p>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm sm:text-base text-[#8A9099] mb-2 text-left">
                            Your Name
                          </label>
                          <input
                            type="text"
                            placeholder='e.g. "Chiamaka"'
                            value={magnetSlots[activeSlot].name}
                            onChange={(e) =>
                              updateSlot(activeSlot, 'name', e.target.value)
                            }
                            disabled={isAdding}
                            maxLength={60}
                            className="w-full px-4 py-3 border border-[#E4E4E4] bg-[#F5F5F5] text-[#1C2A46] placeholder-[#8A9099] placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent disabled:opacity-60"
                          />
                        </div>

                        <div>
                          <label className="block text-sm sm:text-base text-[#8A9099] mb-2 text-left">
                            Which means:
                          </label>
                          <input
                            type="text"
                            placeholder='e.g., "Amaka — one who is beautiful in spirit and form."'
                            value={magnetSlots[activeSlot].meaning}
                            onChange={(e) =>
                              updateSlot(activeSlot, 'meaning', e.target.value)
                            }
                            disabled={isAdding}
                            maxLength={140}
                            className="w-full px-4 py-3 border border-[#E4E4E4] bg-[#F5F5F5] text-[#1C2A46] placeholder-[#8A9099] placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent disabled:opacity-60"
                          />
                        </div>

                        <div>
                          <label className="block text-sm sm:text-base text-[#8A9099] mb-2 text-left">
                            Story Behind the Name (Optional)
                          </label>
                          <textarea
                            placeholder="Share the story or meaning behind this name. This will be printed behind the magnet."
                            value={magnetSlots[activeSlot].story}
                            onChange={(e) =>
                              updateSlot(activeSlot, 'story', e.target.value)
                            }
                            rows={5}
                            disabled={isAdding}
                            maxLength={2000}
                            className="w-full px-4 py-3 border border-[#E4E4E4] bg-[#F5F5F5] text-[#1C2A46] placeholder-[#8A9099] placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent resize-none disabled:opacity-60"
                          />
                          <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                            {magnetSlots[activeSlot].story.length}/2000
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 sm:mb-8">
                      <h3 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-2">
                        Personalise your magnet
                      </h3>
                      <p className="text-sm sm:text-base text-[#6B635D] mb-2">
                        Each magnet holds one photo and one story.
                      </p>

                      <label
                        className={`block border-2 mb-6 sm:mb-7 flex-col justify-center items-center border-dashed border-[#DBDBDB] bg-[#F5F5F5] p-8 sm:p-12 text-center transition-colors ${
                          isAdding
                            ? 'cursor-not-allowed opacity-60'
                            : 'hover:border-[#D2A146] cursor-pointer'
                        }`}
                        onClick={() => {
                          if (!isAdding) setShowPersonalizeModal(true);
                        }}
                      >
                        <div className="flex justify-center items-center">
                          <UploadCloudIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-3 sm:mb-4" />
                        </div>
                        <p className="text-base sm:text-lg font-medium text-gray-700 mb-2">
                          Upload photos & add stories
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          PNG, JPG, JPEG (max size. 10mb)
                        </p>
                      </label>

                      {personalizedMagnets.length > 0 && (
                        <p className="text-sm text-[#2F8F5B] text-center">
                          ✅ {personalizedMagnets.length} magnet
                          {personalizedMagnets.length !== 1 ? 's' : ''} added
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                    <div className="mb-4 sm:mb-6">
                      <span className="text-2xl sm:text-3xl font-medium text-[#1C2A46]">
                        ₦{(price * quantity).toLocaleString()}
                      </span>
                      <div className="text-xs sm:text-sm text-[#6B635D] mt-1">
                        {quantity} set of magnet
                      </div>
                    </div>

                    <div className="flex-col items-start w-full">
                      {renderAddToBagButton()}

                      {isNamesOfGodCustomMode &&
                        !magnetSlots.some(
                          (s) => s.name.trim() && s.meaning.trim()
                        ) && (
                          <p className="text-amber-600 text-xs sm:text-sm mt-2">
                            Add at least one name and its meaning to continue
                          </p>
                        )}

                      {isCustom &&
                        !isNamesOfGodProduct &&
                        personalizedMagnets.length === 0 && (
                          <p className="text-amber-600 text-xs sm:text-sm mt-2">
                            Please upload at least one photo to continue
                          </p>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6 sm:space-y-8 text-left">
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C2A46] mb-3 sm:mb-4">
                    {title}
                  </h1>
                  <p className="text-base sm:text-lg text-[#6B635D] leading-relaxed">
                    {description}
                  </p>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-medium text-[#1C2A46] mb-2">
                    What Included
                  </h3>
                  <div className="bg-[#F5F5F5] px-3 sm:px-4 py-4 sm:py-5">
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                        <span className="text-[#1C2A46] text-sm sm:text-base">
                          4 memory magnets
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                        <span className="text-[#1C2A46] text-sm sm:text-base">
                          Story behind each image
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center justify-between space-x-4 w-32 sm:w-36 p-1.5 border border-[#E0E0E0]">
                      <button
                        onClick={() => setQuantity((p) => Math.max(1, p - 1))}
                        className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                        disabled={!inStock || isAdding}
                      >
                        <MinusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                      </button>
                      <span className="text-sm sm:text-base font-normal text-[#1C2A46] min-w-[2.5rem] sm:min-w-[3rem] text-center">
                        {quantity} set
                      </span>
                      <button
                        onClick={() => setQuantity((p) => p + 1)}
                        className="rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                        disabled={!inStock || isAdding}
                      >
                        <PlusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                      </button>
                    </div>
                  </div>

                  <p className="text-xs sm:text-sm text-[#55000C] italic mb-6 sm:mb-8">
                    Each set contains 4 memory magnets
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                    <div className="mb-4 sm:mb-6">
                      <span className="text-2xl sm:text-3xl font-medium text-[#1C2A46]">
                        ₦{(price * quantity).toLocaleString()}
                      </span>
                      <div className="text-xs sm:text-sm text-[#6B635D] mt-1">
                        {quantity} set of magnet
                      </div>
                    </div>

                    <div className="flex-1 w-full">
                      {renderAddToBagButton()}

                      {!inStock && (
                        <p className="text-red-600 text-xs sm:text-sm mt-2 text-center">
                          This item is currently out of stock
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {!isCustom && !isNamesOfGodCustomMode && (
          <>
            <div className="text-left mt-16 sm:mt-24 md:mt-32 mb-6 sm:mb-8">
              <h3 className="text-2xl sm:text-3xl font-medium text-[#1C2A46] mb-6 sm:mb-8 border-b-2 pb-4 sm:pb-6 border-bordercolor">
                Product Details
              </h3>
              <p className="text-sm sm:text-base text-[#6B635D] mb-6 sm:mb-8 leading-relaxed">
                {detailedDescription ||
                  `Erin-Ijesha Waterfalls (also known as Olumirin waterfalls) is located in Erin-odo, Osun State. It is a tourist attraction located in Oriade local government area, Osun State, Nigeria. The waterfalls were discovered in 1140 AD by one of the daughters of Oduduwa.`}
              </p>

              <h4 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-3 sm:mb-4">
                Specifications
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 text-sm">
                <div>
                  <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      Magnet Count
                    </span>
                    <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                      6 pieces
                    </span>
                  </div>
                  <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      Size
                    </span>
                    <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                      3" x 3" each
                    </span>
                  </div>
                  <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      Material
                    </span>
                    <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                      Premium vinyl
                    </span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      Finish
                    </span>
                    <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                      Glossy, water-resistant
                    </span>
                  </div>
                  <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      Packaging
                    </span>
                    <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                      Gift box with ribbon
                    </span>
                  </div>
                  <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                    <span className="text-[#1C2A46] text-sm sm:text-base">
                      Made In
                    </span>
                    <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                      Nigeria
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <CustomerReviews
              slug={PRODUCT_SLUG}
              onWriteReview={() => setShowReviewModal(true)}
              refreshKey={reviewsRefreshKey}
            />
          </>
        )}
      </div>

      <PersonalizeModal
        key={personalizeModalKey}
        isOpen={showPersonalizeModal}
        onClose={() => setShowPersonalizeModal(false)}
        onSave={handlePersonalizeSave}
        onProgressChange={handlePersonalizeProgress}
      />

      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setReviewsRefreshKey((k) => k + 1);
        }}
        product={{
          id: PRODUCT_SLUG,
          title: displayTitle,
          image: image,
          slug: PRODUCT_SLUG,
          price: price,
          category: category || 'Memory Magnet',
          description: displayDescription,
          location: magnetLocation,
          inStock: inStock,
          isCustom: isCustom || isNamesOfGodCustomMode
        }}
      />

      <style jsx global>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }

        .flip-card-main {
          perspective: 1000px;
          position: relative;
          width: 100%;
          height: 100%;
        }
        .flip-card-front-main,
        .flip-card-back-main {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          transition: transform 0.6s ease-in-out;
          transform-style: preserve-3d;
          border-radius: 2px;
        }
        .flip-card-front-main { transform: rotateY(0deg); }
        .flip-card-front-main.flipped { transform: rotateY(-180deg); }
        .flip-card-back-main { transform: rotateY(180deg); }
        .flip-card-back-main.flipped { transform: rotateY(0deg); }

        .flip-card-thumb {
          perspective: 1000px;
          position: relative;
          width: 100%;
          height: 100%;
        }
        .flip-card-front-thumb,
        .flip-card-back-thumb {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          transition: transform 0.4s ease-in-out;
          transform-style: preserve-3d;
          border-radius: 2px;
        }
        .flip-card-front-thumb { transform: rotateY(0deg); }
        .flip-card-front-thumb.flipped { transform: rotateY(-180deg); }
        .flip-card-back-thumb { transform: rotateY(180deg); }
        .flip-card-back-thumb.flipped { transform: rotateY(0deg); }

        @media (max-width: 768px) {
          .group:active .flip-card-front-main,
          .group:active .flip-card-front-thumb {
            transform: rotateY(-180deg);
          }
          .group:active .flip-card-back-main,
          .group:active .flip-card-back-thumb {
            transform: rotateY(0deg);
          }
        }
      `}</style>
    </div>
  );
};

export default MemoryMagnetEdit;